import StageSkillsProfile, { SkillsProfileSkill } from 'components/feature/StageSkillsProfile/StageSkillsProfile';
import {
  InterviewStageFocus,
  useInterviewStageUpdateMutation,
  useOverwriteInterviewStageSkillsMutation,
  useProjectAddInterviewStageMutation,
  useProjectJobRoleFindManyQuery,
} from 'generated/graphql';
import { isEqual } from 'lodash';
import { FC, useState } from 'react';
import {
  getInterviewStageDurationLabel,
  getInterviewStageFocusLabel,
  INTERVIEW_STAGE_DURATION_DEFAULT_VALUE,
  STAGE_NAME_MAX_LENGTH,
} from 'views/Project/components/Stages/helpers';
import { ApolloError } from '@apollo/client';
import {
  EditFormWrapper,
  Loader,
  Select,
  TextInput,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  InterviewStageFormProps,
  InterviewStageFormValues,
} from './InterviewStageForm.interfaces';
import styles from './InterviewStageForm.module.scss';

const focusFieldOptions = Object.values(InterviewStageFocus).map(focus => ({
  value: focus,
  label: getInterviewStageFocusLabel(focus),
}));

const durationFieldOptions = [
  { value: 1800, label: getInterviewStageDurationLabel(1800) },
  { value: 2700, label: getInterviewStageDurationLabel(2700) },
  {
    value: INTERVIEW_STAGE_DURATION_DEFAULT_VALUE,
    label: getInterviewStageDurationLabel(
      INTERVIEW_STAGE_DURATION_DEFAULT_VALUE
    ),
    labelDescription: 'Default',
  },
  { value: 5400, label: getInterviewStageDurationLabel(5400) },
];

const StageForm: FC<InterviewStageFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  interviewStageSubId,
  projectSubId,
  projectId,
}) => {
  const [formData, setFormData] =
    useState<InterviewStageFormValues>(initialValues);
  const [loading, setLoading] = useState(false);

  const isCreate = !interviewStageSubId;

  const durationFieldValue = durationFieldOptions.find(
    option => option.value === formData.duration
  );

  const focusFieldValue = focusFieldOptions.find(
    option => option.value === formData.focus
  );

  const { data: projectJobRoleData, loading: projectJobRoleLoading } = useProjectJobRoleFindManyQuery({
    variables: {
      id: projectId,
    },
    skip: !projectId,
  });

  const skillsProfileId =
    projectJobRoleData?.ProjectJobRoleFindMany?.find(role => role.isMainRole)?.skillsProfileId;

  const { handleMsgType } = useNotification();
  const [updateInterviewStage] = useInterviewStageUpdateMutation();

  const [overwriteInterviewStageSkills] =
    useOverwriteInterviewStageSkillsMutation();

  const [projectAddInterviewStage] = useProjectAddInterviewStageMutation();

  const handleUpdateInterviewStage = async () => {
    if (!interviewStageSubId) {
      return;
    }

    const toUpdateInterviewStage = !isEqual(
      {
        name: formData.name.trim(),
        duration: formData.duration,
        focus: formData.focus,
      },
      {
        name: initialValues.name,
        duration: initialValues.duration,
        focus: initialValues.focus,
      }
    );

    const toOverwriteInterviewStageSkills =
      !isEqual(formData.softSkills, initialValues.softSkills) ||
      !isEqual(formData.technicalSkills, initialValues.technicalSkills);

    if (!toUpdateInterviewStage && !toOverwriteInterviewStageSkills) {
      handleMsgType({
        type: TNotification.success,
        title: 'Successfully updated interview stage',
        dismissable: true,
      });
      onSubmit();
      return;
    }

    setLoading(true);
    try {
      if (toUpdateInterviewStage) {
        await updateInterviewStage({
          variables: {
            subId: interviewStageSubId,
            data: {
              name: formData.name.trim(),
              duration: formData.duration,
              focus: formData.focus,
            },
          },
        });
      }

      if (toOverwriteInterviewStageSkills) {
        await overwriteInterviewStageSkills({
          variables: {
            id: interviewStageSubId,
            skillsProfileId: skillsProfileId || 0,
            softSkillIds: formData.softSkills.map(skill => skill.id),
            technicalSkillIds: formData.technicalSkills.map(skill => skill.id),
          },
        });
      }
      handleMsgType({
        type: TNotification.success,
        title: 'Successfully updated interview stage',
        dismissable: true,
      });
      onSubmit();
    } catch (error) {
      handleMsgType({
        type: TNotification.error,
        title: 'Failed to update interview stage',
        message: (error as ApolloError)?.message,
        dismissable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInterviewStage = async () => {
    try {
      setLoading(true);
      const createInterviewStageResponse = await projectAddInterviewStage({
        variables: {
          subId: projectSubId,
          ...formData,
          name: formData.name.trim(),
        },
      });
      // Interview stage names are unique, so we can find the created interview stage by name.
      const createdInterviewStage =
        createInterviewStageResponse.data?.ProjectAddInterviewStage?.interviewStages.find(
          stage => stage.name === formData.name
        );

      if (
        createdInterviewStage?.subId &&
        (formData.softSkills.length > 0 || formData.technicalSkills.length > 0)
      ) {
        await overwriteInterviewStageSkills({
          variables: {
            id: createdInterviewStage.subId,
            skillsProfileId: skillsProfileId || 0,
            softSkillIds: formData.softSkills.map(skill => skill.id),
            technicalSkillIds: formData.technicalSkills.map(skill => skill.id),
          },
        });
      }
      onSubmit();
    } catch (_error) {
      handleMsgType({
        type: TNotification.error,
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForm = async () => {
    if (loading) {
      return;
    }
    if (formData.name.trim().length === 0 || formData.name.length > STAGE_NAME_MAX_LENGTH) {
      return;
    }
    if (isCreate) {
      handleCreateInterviewStage();
    } else {
      handleUpdateInterviewStage();
    }
  };

  const handleUpdateSoftSkills = (softSkill: SkillsProfileSkill) => {
    const currentSkills = formData.softSkills ?? [];
    const exists = currentSkills.some(skill => skill.id === softSkill.id);
    setFormData(prev => ({
      ...prev,
      softSkills: exists
        ? currentSkills.filter(skill => skill.id !== softSkill.id)
        : [...currentSkills, softSkill],
    }));
  };

  const handleUpdateTechnicalSkills = (technicalSkill: SkillsProfileSkill) => {
    const currentSkills = formData.technicalSkills ?? [];
    const exists = currentSkills.some(skill => skill.id === technicalSkill.id);
    setFormData(prev => ({
      ...prev,
      technicalSkills: exists
        ? currentSkills.filter(skill => skill.id !== technicalSkill.id)
        : [...currentSkills, technicalSkill],
    }));
  };

  if (loading || projectJobRoleLoading) {
    return <Loader variant="bubbles" />;
  }

  return (
    <EditFormWrapper
      title={`${isCreate ? 'Create' : 'Edit'} Interview stage`}
      hasResetAllButton={false}
      onCancelClick={onCancel}
      onSaveClick={handleSaveForm}
    >
      <div className={styles.container}>
        <h3 className={styles.sectionTitle}>General</h3>

        <span>Interview name</span>
        <TextInput
          id="interview-name"
          onChange={value => {
            setFormData(prev => ({ ...prev, name: value }));
          }}
          value={formData.name}
          maxLength={STAGE_NAME_MAX_LENGTH}
          maxCharacters={STAGE_NAME_MAX_LENGTH}
          maxCharactersError={formData.name.length >= STAGE_NAME_MAX_LENGTH}
          hasError={formData.name.trim().length === 0 || formData.name.length > STAGE_NAME_MAX_LENGTH}
          required
        />

        <span>Interview focus</span>
        <Select<InterviewStageFocus>
          id="interview_stage_focus"
          hasClearIndicator={false}
          options={focusFieldOptions}
          value={focusFieldValue}
          onChange={option =>
            setFormData(prev => ({ ...prev, focus: option.value! }))
          }
        />
        <span>Interview duration</span>
        <Select<number>
          id="interview_stage_duration"
          hasClearIndicator={false}
          options={durationFieldOptions}
          value={durationFieldValue}
          keepSelectedItemInPlace
          onChange={option =>
            setFormData(prev => ({ ...prev, duration: option.value! }))
          }
        />
        <StageSkillsProfile
          softSkills={formData.softSkills}
          technicalSkills={formData.technicalSkills}
          onUpdateSoftSkills={handleUpdateSoftSkills}
          onUpdateTechnicalSkills={handleUpdateTechnicalSkills}
        />
      </div>
    </EditFormWrapper>
  );
};

export default StageForm;
